import { usePage } from "@/components/page/PageContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatGateAssignmentType } from "@/lib/format";
import { compareGateNames } from "@/lib/gateSort";
import {
  CONCOURSE_NAMES,
  concourseFromTabValue,
  concourseTabValue,
  flightConcourse,
  usesConcourses,
} from "@/lib/concourse";
import { components } from "@/lib/openapi";
import { getFlightData, toggleFlightStar } from "@/queries/flights";
import { useReportContext } from "@/stores/ReportContext";
import { useSessionData } from "@/stores/SessionDataContext";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  StarFilledIcon,
  StarIcon,
  CaretSortIcon,
} from "@radix-ui/react-icons";
import { AlertCircle, CheckCircle, TrendingUp, XCircle } from "lucide-react";
import { clsx } from "clsx";
import _ from "lodash";
import { Settings2 } from "lucide-react";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import ScrollToTop from "react-scroll-to-top";

type Flight = components["schemas"]["FlightSchema"];
type FlightWithDirection = Flight & { direction: "arrival" | "departure" };
type SortKey = keyof FlightWithDirection;

type StatusType = "on_time" | "early" | "delayed" | "canceled";

interface StatusConfig {
  type: StatusType;
  label: string;
  icon: React.ReactNode;
  badgeClass: string;
}

// Helper function to determine status from display text
const getStatusConfig = (statusText: string | undefined): StatusConfig => {
  if (!statusText) {
    return {
      type: "on_time",
      label: "On Time",
      icon: <CheckCircle className="w-3.5 h-3.5" />,
      badgeClass:
        "bg-green-500/20 text-green-300 border border-green-500/30 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700/50",
    };
  }

  const normalized = statusText.toLowerCase();

  if (normalized.includes("canceled") || normalized.includes("cancel")) {
    return {
      type: "canceled",
      label: "Canceled",
      icon: <XCircle className="w-3.5 h-3.5" />,
      badgeClass:
        "bg-red-500/20 text-red-300 border border-red-500/30 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700/50",
    };
  }

  if (normalized.includes("delayed") || normalized.includes("delay")) {
    return {
      type: "delayed",
      label: "Delayed",
      icon: <AlertCircle className="w-3.5 h-3.5" />,
      badgeClass:
        "bg-amber-500/20 text-amber-300 border border-amber-500/30 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700/50",
    };
  }

  if (normalized.includes("early")) {
    return {
      type: "early",
      label: "Early",
      icon: <TrendingUp className="w-3.5 h-3.5" />,
      badgeClass:
        "bg-blue-500/20 text-blue-300 border border-blue-500/30 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700/50",
    };
  }

  return {
    type: "on_time",
    label: "On Time",
    icon: <CheckCircle className="w-3.5 h-3.5" />,
    badgeClass:
      "bg-green-500/20 text-green-300 border border-green-500/30 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700/50",
  };
};

function hmToMinutes(value: string | null | undefined): number {
  if (value == null || value === "") {
    return -1;
  }
  const parts = value.split(":").map(Number);
  if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) {
    return -1;
  }
  const [hours, minutes] = parts;
  return hours * 60 + minutes;
}

const useFilteredSortedFlights = (
  flights: FlightWithDirection[],
  terminal: string,
  sortKey: SortKey | "chronological" | null,
  sortAsc: boolean,
  showPast: boolean,
  showArrivals: boolean,
  showDepartures: boolean,
  code_iata: string
) => {
  return useMemo(() => {
    if (!flights) {
      return [];
    }

    let filtered = flights.filter((flight) => {
      if (terminal !== "0") {
        if (usesConcourses(code_iata)) {
          if (flightConcourse(flight) !== concourseFromTabValue(terminal))
            return false;
        } else {
          if (flight.terminal !== terminal) return false;
        }
      }

      if (!showArrivals && flight.direction === "arrival") return false;
      if (!showDepartures && flight.direction === "departure") return false;

      const now = new Date();
      const currentTime = now.getHours() * 60 + now.getMinutes();
      const flightTime =
        flight.direction === "arrival"
          ? flight.sch_arr_time
          : flight.sch_dep_time;
      const [hours, minutes] = flightTime.split(":").map(Number);
      const flightTimeMinutes = hours * 60 + minutes;

      if (!showPast && flightTimeMinutes < currentTime) return false;

      return true;
    });

    if (sortKey === "chronological") {
      const order = sortAsc ? "asc" : "desc";
      filtered = _.orderBy(
        filtered,
        [
          (f) => {
            const timeStr = f.direction === "arrival" ? f.arr_time : f.dep_time;
            return hmToMinutes(timeStr);
          },
          (f) => f.id,
        ],
        [order, "asc"]
      );
    } else if (sortKey) {
      const order = sortAsc ? "asc" : "desc";
      if (sortKey === "dep_time") {
        filtered = _.orderBy(
          filtered,
          [(f) => hmToMinutes(f.dep_time), (f) => f.id],
          [order, "asc"]
        );
      } else if (sortKey === "arr_time") {
        filtered = _.orderBy(
          filtered,
          [(f) => hmToMinutes(f.arr_time), (f) => f.id],
          [order, "asc"]
        );
        } else if (sortKey === "gate") {
        filtered = [...filtered].sort((a, b) => {
          const aGate = a.gate ?? "";
          const bGate = b.gate ?? "";
          if (!aGate && bGate) return 1;
          if (aGate && !bGate) return -1;
          if (!aGate && !bGate) return a.id - b.id;
          const comparison = compareGateNames(aGate, bGate);
          if (comparison !== 0) {
            return sortAsc ? comparison : -comparison;
          }
          return a.id - b.id;
        });
      } else {
        filtered = _.orderBy(filtered, [sortKey], [order]);
      }
    }

    return filtered;
  }, [
    flights,
    terminal,
    sortKey,
    sortAsc,
    showPast,
    showArrivals,
    showDepartures,
    code_iata,
  ]);
};

export const RetailFlightsPage: FunctionComponent = () => {
  const [terminal, setTerminal] = useState("0");
  const [sortKey, setSortKey] = useState<SortKey | "chronological" | null>(
    "chronological"
  );
  const [sortAsc, setSortAsc] = useState(true);
  const [showPast, setShowPast] = useState(true);
  const [showArrivals, setShowArrivals] = useState(true);
  const [showDepartures, setShowDepartures] = useState(true);

  const { terminals, code_iata } = useSessionData();
  const showConcourse = usesConcourses(code_iata);
  const { reportId } = useReportContext();
  const { data: arrivalData, isLoading: arrivalsLoading } = getFlightData(
    reportId,
    "arrival"
  );
  const { data: departureData, isLoading: departuresLoading } = getFlightData(
    reportId,
    "departure"
  );
  const { mutate: toggleArrivalStar } = toggleFlightStar(reportId, "arrival");
  const { mutate: toggleDepartureStar } = toggleFlightStar(
    reportId,
    "departure"
  );

  const allFlights: FlightWithDirection[] = useMemo(() => {
    const arrivals: FlightWithDirection[] = (arrivalData || []).map(
      (flight) => ({
        ...flight,
        direction: "arrival" as const,
      })
    );
    const departures: FlightWithDirection[] = (departureData || []).map(
      (flight) => ({
        ...flight,
        direction: "departure" as const,
      })
    );
    return [...arrivals, ...departures];
  }, [arrivalData, departureData]);

  const flightDataLoading = arrivalsLoading || departuresLoading;

  const filteredData: FlightWithDirection[] = useFilteredSortedFlights(
    allFlights,
    terminal,
    sortKey,
    sortAsc,
    showPast,
    showArrivals,
    showDepartures,
    code_iata
  );

  const { setTitles } = usePage();
  useEffect(() => {
    setTitles(["Flights"]);
  }, []);

  const handleToggleStar = (
    flightId: number,
    direction: "arrival" | "departure"
  ) => {
    if (direction === "arrival") {
      toggleArrivalStar(flightId);
    } else {
      toggleDepartureStar(flightId);
    }
  };

  const setSort = (key: SortKey | "chronological") => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
    }
  };

  function renderTableHeader(
    key: SortKey | "chronological",
    name: string,
    sortable: boolean,
    width: string
  ) {
    const handleClick = () => {
      if (sortable) {
        setSort(key);
      }
    };

    return (
      <th className={`${width} !text-left !p-2 !px-4 font-medium text-slate-400`} onClick={sortable ? handleClick : undefined}>
        {sortable ? (
          <div
            className="flex items-center gap-1.5 py-1.5 cursor-pointer hover:text-slate-200 transition-colors select-none w-fit"
          >
            <span className="text-sm">{name}</span>
            {key === sortKey ? (
              sortAsc ? (
                <ChevronUpIcon className="w-4 h-4 shrink-0 text-slate-400" />
              ) : (
                <ChevronDownIcon className="w-4 h-4 shrink-0 text-slate-400" />
              )
            ) : (
              <CaretSortIcon className="w-4 h-4 shrink-0 text-slate-500 opacity-70" />
            )}
          </div>
        ) : (
          <div className="flex py-1.5 items-center text-sm select-none">{name}</div>
        )}
      </th>
    );
  }

  return (
    <div className={"mx-4 md:mx-12 mt-8"}>
      <div className="flex flex-wrap items-center justify-between gap-3 w-full">
        <div className="overflow-x-auto">
          <Tabs value={terminal} onValueChange={(value) => setTerminal(value)}>
            <TabsList>
              <TabsTrigger value="0">
                All {showConcourse ? "Concourses" : "Terminals"}
              </TabsTrigger>
              {showConcourse
                ? CONCOURSE_NAMES.map((name) => (
                    <TabsTrigger value={concourseTabValue(name)} key={name}>
                      {concourseTabValue(name)}
                    </TabsTrigger>
                  ))
                : terminals.map((t) => (
                    <TabsTrigger value={t.terminal_name} key={t.id}>
                      {t.terminal_name}
                    </TabsTrigger>
                  ))}
            </TabsList>
          </Tabs>
        </div>
        <div className="flex items-center gap-4">
          <div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="show-past"
                checked={showPast}
                onCheckedChange={(val) =>
                  val !== "indeterminate" && setShowPast(val)
                }
              />
              <label
                htmlFor="show-past"
                className="text-sm font-medium leading-none whitespace-nowrap peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Show Past Flights
              </label>
            </div>
          </div>
          <div>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 border-dashed"
                >
                  <Settings2 className="mr-2 h-4 w-4" />
                  Display Options
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0" align="start">
                <div className="p-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label
                        htmlFor="show-arrivals"
                        className="text-sm font-medium"
                      >
                        Show Arrivals
                      </Label>
                      <Switch
                        id="show-arrivals"
                        checked={showArrivals}
                        onCheckedChange={setShowArrivals}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label
                        htmlFor="show-departures"
                        className="text-sm font-medium"
                      >
                        Show Departures
                      </Label>
                      <Switch
                        id="show-departures"
                        checked={showDepartures}
                        onCheckedChange={setShowDepartures}
                      />
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
      {flightDataLoading && (
        <div className={"mt-4 text-sm dark:text-slate-300"}>
          Please wait while we load your flights
        </div>
      )}
      <div className="overflow-auto max-h-[calc(100vh-12rem)] border border-slate-800/60 rounded-md mt-4">
        {filteredData.length > 0 && (
          <table className={"flight-table w-full relative !mt-0 !overflow-visible"}>
            <thead className="sticky top-0 z-10 bg-[#0b1120] border-b border-slate-700/60">
              <tr>
                <th className={"w-8"}></th>
                {renderTableHeader("direction", "Direction", true, "w-32")}
                {renderTableHeader("number", "Flight", true, "w-32")}
                {renderTableHeader("dep_time", "Departure", true, "w-44")}
                {renderTableHeader("arr_time", "Arrival", true, "w-44")}
                {renderTableHeader("delay", "Status", true, "w-56")}
                {renderTableHeader("terminal", "Terminal", true, "w-20")}
                {showConcourse &&
                  renderTableHeader("concourse", "Concourse", true, "w-24")}
                {renderTableHeader("gate", "Gate", true, "w-20")}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((row) => {
                const statusDisplay =
                  row.direction === "arrival"
                    ? row.arrival_status_display
                    : row.departure_status_display;
                const statusConfig = getStatusConfig(statusDisplay);

                return (
                  <tr
                    key={`${row.direction}-${row.id}`}
                    className={clsx(
                      // Direction-based row styling
                      row.direction === "arrival"
                        ? "bg-gradient-to-r from-blue-500/10 via-transparent to-transparent hover:from-blue-500/15 dark:from-blue-900/20 dark:hover:from-blue-900/30"
                        : "bg-gradient-to-r from-emerald-500/10 via-transparent to-transparent hover:from-emerald-500/15 dark:from-emerald-900/20 dark:hover:from-emerald-900/30",
                      // Starred row styling
                      row.starred && "!bg-amber-900/40 dark:!bg-amber-900/30",
                      "transition-colors duration-150"
                    )}
                  >
                    <td
                      className={"cursor-pointer"}
                      onClick={() => handleToggleStar(row.id, row.direction)}
                    >
                      {row.starred ? (
                        <StarFilledIcon className={"ml-1 text-amber-200"} />
                      ) : (
                        <StarIcon className={"ml-1"} />
                      )}
                    </td>
                    {/* Enhanced Direction Badge */}
                    <td>
                      <Badge
                        className={clsx(
                          "uppercase font-semibold border",
                          row.direction === "arrival"
                            ? "bg-blue-500/20 text-blue-300 border-blue-500/30 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700/50"
                            : "bg-emerald-500/20 text-emerald-300 border-emerald-500/30 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700/50"
                        )}
                        variant="secondary"
                      >
                        {row.direction === "arrival"
                          ? "↓ Arrival"
                          : "↑ Departure"}
                      </Badge>
                    </td>
                    <td>
                      <span className="font-semibold">{row.number}</span>
                    </td>
                    <td>
                      <div className="flex flex-col">
                        <span className="font-semibold">{row.dep_iata}</span>
                        <span className="text-xs opacity-70">
                          Sch. {row.sch_dep_time}, Act. {row.dep_time}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="flex flex-col">
                        <span className="font-semibold">{row.arr_iata}</span>
                        <span className="text-xs opacity-70">
                          Sch. {row.sch_arr_time}, Act. {row.arr_time}
                        </span>
                      </div>
                    </td>
                    {/* Enhanced Status Badge */}
                    <td>
                      <div className="flex flex-col gap-1 justify-center items-center">
                        {(() => {
                          const isCancelled =
                            statusConfig.type === "canceled" ||
                            row.status_oag?.toLowerCase().includes("cancel") ||
                            false;
                          const badgeClass = isCancelled
                            ? "bg-red-500/20 text-red-300 border border-red-500/30 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700/50"
                            : statusConfig.badgeClass;
                          const icon = isCancelled ? (
                            <XCircle className="w-3.5 h-3.5" />
                          ) : (
                            statusConfig.icon
                          );
                          const label = isCancelled
                            ? "Canceled"
                            : statusConfig.label;

                          return (
                            <div
                              className={clsx(
                                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium w-fit",
                                badgeClass
                              )}
                            >
                              {/* {icon} */}
                              <span>
                                {(statusConfig.type === "delayed" ||
                                  statusConfig.type === "early") &&
                                !isCancelled
                                  ? ` ${statusConfig.type === "early" ? "-" : "+"}${row.direction === "arrival" ? row.arrival_status_display : row.departure_status_display}`
                                  : ""}
                                {(isCancelled ||
                                  statusConfig.type === "on_time") &&
                                  label}
                              </span>
                            </div>
                          );
                        })()}
                        {row.status_oag && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {row.status_oag}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-center">
                      {row.terminal && (
                        <Badge variant="secondary">{row.terminal}</Badge>
                      )}
                    </td>
                    {showConcourse && (
                      <td className="text-center">
                        {row.concourse && (
                          <Badge variant="secondary">{row.concourse}</Badge>
                        )}
                      </td>
                    )}
                    <td className="text-center">
                      {row.gate && (
                        <Badge
                          variant="secondary"
                          className={clsx(row.gate_guess_flag && "opacity-70")}
                        >
                          {row.gate_guess_flag && "*"}
                          {row.gate}
                        </Badge>
                      )}
                    </td>
                    {/* Removed Claim, Aircraft, Logic columns for Retail */}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <ScrollToTop smooth className={"!bg-slate-600 pl-1.5"} />
    </div>
  );
};
